import { changeInCentsPerLb, formatUsdPerLb, shiftMonth } from "./callForm";
import { monthName } from "./format";
import type { NominationBoard } from "./nominationBoard";

export type InsightTone = "ok" | "warn" | "neutral";

export interface Insight {
  tone: InsightTone;
  heading: string;
  detail: string;
  mathSteps: string[];
}

export interface CallComparison {
  currentFmpUsdPerLb: number | null;
  priorFmpUsdPerLb: number | null;
  priorCallMonth: string | null;
}

export const TREND_RULE =
  "The rule: both up = pushing harder, both down = easing off, both flat = same asks, one each way = mixed.";

const WHY_AMBER_MATTERS =
  "Why amber matters: prices rarely land outside what suppliers ask for, so a call above the highest ask or below the lowest needs a reason.";

function roundCents(cents: number): number {
  return Math.round(cents * 100) / 100;
}

export function signedCents(cents: number): string {
  return `${cents > 0 ? "+" : ""}${cents}¢`;
}

function centsRange(lowestCents: number, highestCents: number): string {
  if (lowestCents === highestCents) return signedCents(lowestCents);
  return `${signedCents(lowestCents)} to ${signedCents(highestCents)}`;
}

function askPhrase(cents: number): string {
  if (cents === 0) return "no change (rollover)";
  return cents > 0 ? `+${cents}¢/lb` : `${cents}¢/lb (a decrease)`;
}

function predictedMovement(changeCents: number): string {
  if (changeCents === 0) return "stay flat";
  return changeCents > 0 ? `go up ${changeCents}¢/lb` : `go down ${Math.abs(changeCents)}¢/lb`;
}

function askMovementWords(changeCents: number): string {
  if (changeCents === 0) return "unchanged";
  return changeCents > 0 ? `up ${changeCents}¢` : `down ${Math.abs(changeCents)}¢`;
}

function askMovementLabel(changeCents: number): string {
  return changeCents === 0 ? "unchanged" : signedCents(changeCents);
}

export function callCheckInsight(
  resinFamilyCode: string,
  month: string,
  comparison: CallComparison,
  board: NominationBoard,
): Insight {
  const { currentFmpUsdPerLb, priorFmpUsdPerLb, priorCallMonth } = comparison;
  if (currentFmpUsdPerLb === null) {
    return {
      tone: "neutral",
      heading: `No ${resinFamilyCode} call yet for ${monthName(month)}`,
      detail: "Save your call below to check it against supplier asks.",
      mathSteps: [
        "What it will check: once you save a call, your predicted change (this month's FMP − your previous call's FMP) is compared with the lowest and highest supplier asks.",
        "A change inside that range shows green. A change above the highest ask or below the lowest shows amber.",
      ],
    };
  }

  const predictingStep = `What you're predicting: this month you called ${formatUsdPerLb(currentFmpUsdPerLb)}.`;
  if (priorFmpUsdPerLb === null || priorCallMonth === null) {
    return {
      tone: "neutral",
      heading: `${formatUsdPerLb(currentFmpUsdPerLb)} call, no earlier call to compare`,
      detail: "Your change is measured against your previous call, and you don't have one yet.",
      mathSteps: [
        predictingStep,
        "There's no earlier call to measure a change from, so the check can't run yet. It starts working from your second month of calls.",
      ],
    };
  }

  const changeCents = changeInCentsPerLb(currentFmpUsdPerLb, priorFmpUsdPerLb);
  const priorMonthName = monthName(priorCallMonth);
  const changeStep =
    `What you're predicting: this month you called ${formatUsdPerLb(currentFmpUsdPerLb)}. ` +
    `Your previous call (${priorMonthName}) was ${formatUsdPerLb(priorFmpUsdPerLb)}. ` +
    `So you're predicting prices ${predictedMovement(changeCents)} (${formatUsdPerLb(currentFmpUsdPerLb)} − ${formatUsdPerLb(priorFmpUsdPerLb)}).`;
  const { lowestAnnouncedCents, highestAnnouncedCents } = board;
  if (lowestAnnouncedCents === null || highestAnnouncedCents === null) {
    return {
      tone: "neutral",
      heading: `${signedCents(changeCents)} call, no supplier asks yet`,
      detail: "Nothing to compare against until a supplier announces a number.",
      mathSteps: [
        changeStep,
        "What suppliers want: no supplier has announced a number yet (they're either No announcement or Not yet checked), so there's nothing to compare against.",
      ],
    };
  }

  const askRange = centsRange(lowestAnnouncedCents, highestAnnouncedCents);
  const suppliersStep =
    lowestAnnouncedCents === highestAnnouncedCents
      ? `What suppliers want: every supplier who announced is asking for ${askPhrase(lowestAnnouncedCents)}.`
      : `What suppliers want: of the suppliers who announced, the smallest ask is ${askPhrase(lowestAnnouncedCents)} and the biggest is ${askPhrase(highestAnnouncedCents)}.`;

  if (changeCents > highestAnnouncedCents) {
    const distanceCents = roundCents(changeCents - highestAnnouncedCents);
    return {
      tone: "warn",
      heading: `${signedCents(changeCents)} call, ${distanceCents}¢ above every supplier ask`,
      detail: `Suppliers asked ${askRange}. Re-check your call or note why in your rationale.`,
      mathSteps: [
        changeStep,
        suppliersStep,
        `The check: your ${signedCents(changeCents)} is bigger than every supplier's ask. It's ${distanceCents}¢ above the highest (${signedCents(changeCents)} − ${signedCents(highestAnnouncedCents)}), so the box is amber.`,
        WHY_AMBER_MATTERS,
      ],
    };
  }
  if (changeCents < lowestAnnouncedCents) {
    const distanceCents = roundCents(lowestAnnouncedCents - changeCents);
    return {
      tone: "warn",
      heading: `${signedCents(changeCents)} call, ${distanceCents}¢ below every supplier ask`,
      detail: `Suppliers asked ${askRange}. Re-check your call or note why in your rationale.`,
      mathSteps: [
        changeStep,
        suppliersStep,
        `The check: your ${signedCents(changeCents)} is smaller than every supplier's ask. It's ${distanceCents}¢ below the lowest (${signedCents(lowestAnnouncedCents)} − ${signedCents(changeCents)}), so the box is amber.`,
        WHY_AMBER_MATTERS,
      ],
    };
  }
  return {
    tone: "ok",
    heading: `${signedCents(changeCents)} call, inside supplier asks (${askRange})`,
    detail: `Your ${resinFamilyCode} call: ${formatUsdPerLb(currentFmpUsdPerLb)} vs ${formatUsdPerLb(priorFmpUsdPerLb)} in ${priorMonthName}.`,
    mathSteps: [
      changeStep,
      suppliersStep,
      `The check: your ${signedCents(changeCents)} falls between ${signedCents(lowestAnnouncedCents)} and ${signedCents(highestAnnouncedCents)}, so it's in line with what suppliers are asking.`,
      WHY_AMBER_MATTERS,
    ],
  };
}

function trendName(topAskChangeCents: number, lowestAskChangeCents: number): string {
  if (topAskChangeCents === 0 && lowestAskChangeCents === 0) return "Same asks";
  if (topAskChangeCents >= 0 && lowestAskChangeCents >= 0) return "Pushing harder";
  if (topAskChangeCents <= 0 && lowestAskChangeCents <= 0) return "Easing off";
  return "Mixed";
}

const TREND_MEANINGS: Record<string, string> = {
  "Pushing harder":
    "Both moved up (or one up and one unchanged), so suppliers are getting more aggressive. Pressure is building for an increase.",
  "Easing off":
    "Both moved down (or one down and one unchanged), so suppliers are backing off. Pressure is easing.",
  "Same asks": "Neither moved, so supplier pressure is the same as last month.",
  Mixed: "One went up and one went down, so suppliers aren't moving together. Expect more negotiation than usual.",
};

export function lastMonthInsight(
  month: string,
  thisMonthBoard: NominationBoard,
  lastMonthBoard: NominationBoard,
): Insight {
  const lastMonthName = monthName(shiftMonth(month, -1));
  const lastMonthLowest = lastMonthBoard.lowestAnnouncedCents;
  const lastMonthHighest = lastMonthBoard.highestAnnouncedCents;
  if (lastMonthLowest === null || lastMonthHighest === null) {
    return {
      tone: "neutral",
      heading: `No asks logged for ${lastMonthName}`,
      detail: "Nothing to compare this month's asks against.",
      mathSteps: [
        "What it compares: this month's highest and lowest supplier asks against last month's.",
        `No supplier was marked Announced in ${lastMonthName}, so there's nothing to compare yet.`,
      ],
    };
  }

  const lastMonthRange = centsRange(lastMonthLowest, lastMonthHighest);
  const lastMonthStep = `Last month's asks (${lastMonthName}): ${lastMonthRange}/lb.`;
  const thisMonthLowest = thisMonthBoard.lowestAnnouncedCents;
  const thisMonthHighest = thisMonthBoard.highestAnnouncedCents;
  if (thisMonthLowest === null || thisMonthHighest === null) {
    return {
      tone: "neutral",
      heading: "No asks yet this month",
      detail: `${lastMonthName} asks were ${lastMonthRange}, shown for reference.`,
      mathSteps: [lastMonthStep, "No supplier has announced this month yet, so last month is shown for reference."],
    };
  }

  const thisMonthRange = centsRange(thisMonthLowest, thisMonthHighest);
  const topAskChangeCents = roundCents(thisMonthHighest - lastMonthHighest);
  const lowestAskChangeCents = roundCents(thisMonthLowest - lastMonthLowest);
  const trend = trendName(topAskChangeCents, lowestAskChangeCents);
  const heading =
    trend === "Same asks"
      ? `Same asks as ${lastMonthName}`
      : `${trend}: top ask ${askMovementLabel(topAskChangeCents)}, lowest ask ${askMovementLabel(lowestAskChangeCents)} vs ${lastMonthName}`;
  return {
    tone: "neutral",
    heading,
    detail: `${lastMonthName}: ${lastMonthRange}. This month: ${thisMonthRange}.`,
    mathSteps: [
      lastMonthStep,
      `This month's asks: ${thisMonthRange}/lb.`,
      `Highest ask: ${signedCents(thisMonthHighest)} − ${signedCents(lastMonthHighest)} = ${askMovementWords(topAskChangeCents)}. Lowest ask: ${signedCents(thisMonthLowest)} − ${signedCents(lastMonthLowest)} = ${askMovementWords(lowestAskChangeCents)}.`,
      TREND_MEANINGS[trend],
      TREND_RULE,
    ],
  };
}
